import * as React from 'react'
import { SafeAreaView, ScrollView, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import Icon from '@components/icon/Icons'
import Button from '@components/button/Button'
import Radio, { RadioOption } from '@components/radio/Radio'
import SimpleAlert from '@components/modal/SimpleAlert'

import useReducer from '@store/useReducer'
import * as LocationStore from '@store/location/location'
import * as FamilleStore from '@store/famille/famille'
import * as SiteStore from '@store/site/site'

import useI18n from '@i18n/useI18n'

interface Props {
  data: Partial<IncidentForm>[]
  onDelete: (id: string) => void
  onClose: () => void
}

const Delete = ({ data, onClose, onDelete }: Props) => {
  const i18n = useI18n()

  const [selected, setSelected] = React.useState<RadioOption>()

  const places = useReducer(LocationStore.store, (s) => s.places)
  const activities = useReducer(FamilleStore.store, (s) => s.activities)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations.inProgress)

  const onItemDelete = () => {
    if (!!selected) {
      SimpleAlert.open({
        title: i18n.t('common.delete'),
        description: i18n.t('screens.incident.deleting'),
        buttons: [
          {
            label: i18n.t('common.yes'),
            onClick: () => {
              onDelete(selected.value)
              SimpleAlert.close()
            },
          },
          { label: i18n.t('common.no'), onClick: () => SimpleAlert.close() },
        ],
      })
    }
  }

  const renderItem = (item: Partial<IncidentForm>) => {
    const manif = manifestations.find((m) => m.id === item.manifestationId)
    const acts = (item.familles || [])
      .map((ac) => activities.find((a) => a.id === ac))
      .filter(Boolean) as FamilleResponse[]
    const space = places.find((m) => m.id === item.space)
    const location = places.find((m) => m.id === item.location)
    const subLocation = places.find((m) => m.id === item.subLocation)

    const loc = [!!space && space.libelle, !!location && location.libelle, !!subLocation && subLocation.libelle]
      .filter(Boolean)
      .join(' - ')

    return (
      <Item key={item.id}>
        <ItemBloc>
          <Manif>{manif ? manif.libelle : i18n.t('screens.incident.MANIF.empty')}</Manif>
          <Location>{loc}</Location>
        </ItemBloc>

        <Separator />

        <ItemBloc>
          {!!item.isInfrastructure && (
            <Info>{i18n.t(`screens.incident.TYPE.${item.isInfrastructure.toLowerCase()}`)}</Info>
          )}
          {acts.length > 0 && <Activity>{acts.map((a) => a.libelle).join(' / ')}</Activity>}
          {!!item.creationDate && (
            <Info>{i18n.t('screens.incident.START.date', { date: new Date(item.creationDate) })}</Info>
          )}
        </ItemBloc>
      </Item>
    )
  }

  return (
    <>
      <Container>
        <Content>
          <SubTitle>{i18n.t('common.delete')}</SubTitle>
          <DeleteInfo>{i18n.t('screens.incident.delete')}</DeleteInfo>

          <ScrollView>
            <Radio
              data={data.map((d) => ({ value: d.id, renderItem: () => renderItem(d) }))}
              onItemSelected={setSelected}
              value={selected}
            />
          </ScrollView>
        </Content>
        <ButtonContainer>
          <Button label={i18n.t('common.delete')} onClick={onItemDelete} status={selected ? 'active' : 'disabled'} />
        </ButtonContainer>
      </Container>
      <Close activeOpacity={0.9} onPress={onClose}>
        <Icon name="close" size={24} />
      </Close>
    </>
  )
}

export default Delete

const Close = styled(TouchableOpacity)`
  position: absolute;
  top: 0;
  left: 0;
  padding: 15px;
`

const SubTitle = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  text-align: center;
`

const DeleteInfo = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-top: 25px;
  margin-bottom: 15px;
`

const Container = styled(SafeAreaView)`
  justify-content: space-between;
  margin-bottom: 36px;
  flex: 1;
`

const Content = styled(View)`
  padding: 15px;
  flex: 1;
`

const Item = styled(View)`
  background: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.secondaryActive};
  border-radius: 6px;
  margin-left: 10px;
  position: relative;
  flex: 1;
`

const ItemBloc = styled(View)`
  padding: 16px 24px;
  justify-content: center;
`

const Manif = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
`

const Location = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  font-size: 14px;
`

const Activity = styled(Text)`
  ${(props) => props.theme.fonts.Title2};
  font-size: 16px;
`

const Separator = styled(View)`
  height: 1px;
  background-color: ${(props) => props.theme.colors.secondaryActive};
`

const ButtonContainer = styled(View)`
  margin-top: 15px;
  padding: 16px;
`
